// Push Notification Manager
// Handles service worker registration and push subscriptions

class PushNotificationManager {
  constructor() {
    this.registration = null;
    this.subscription = null;
    this.vapidPublicKey = null;
  }

  // Check if push notifications are supported
  static isSupported() {
    return 'serviceWorker' in navigator && 'PushManager' in window;
  }

  // Check current permission status
  static getPermissionStatus() {
    if (!('Notification' in window)) return 'unsupported';
    return Notification.permission;
  }

  // Initialize the service worker
  async init() {
    if (!PushNotificationManager.isSupported()) {
      console.warn('[Push] Push notifications not supported');
      return false;
    }

    try {
      // Register service worker
      this.registration = await navigator.serviceWorker.register('/service-worker.js', {
        scope: '/'
      });
      
      console.debug('[Push] Service worker registered:', this.registration.scope);
      
      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;
      
      // Check for existing subscription
      this.subscription = await this.registration.pushManager.getSubscription();
      
      if (this.subscription) {
        console.debug('[Push] Existing subscription found');
        await this.sendSubscriptionToServer(this.subscription);
      }
      
      return true;
    } catch (error) {
      console.error('[Push] Service worker registration failed:', error);
      return false;
    }
  }

  // Request notification permission and subscribe
  async subscribe() {
    if (!this.registration) {
      throw new Error('Service worker not registered');
    }

    // Request permission
    const permission = await Notification.requestPermission();
    
    if (permission !== 'granted') {
      console.warn('[Push] Notification permission denied');
      return null;
    }

    try {
      // Get VAPID public key from server
      if (!this.vapidPublicKey) {
        const response = await fetch('/api/v1/push/vapid_public_key');
        const data = await response.json();
        this.vapidPublicKey = data.public_key;
      }

      // Subscribe to push notifications
      this.subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKey)
      });

      console.debug('[Push] Push subscription successful');

      // Send subscription to server
      await this.sendSubscriptionToServer(this.subscription);

      return this.subscription;
    } catch (error) {
      console.error('[Push] Push subscription failed:', error);
      return null;
    }
  }

  // Unsubscribe from push notifications
  async unsubscribe() {
    if (!this.subscription) {
      return true;
    }

    try {
      const sub = this.subscription;
      await this.subscription.unsubscribe();
      await this.removeSubscriptionFromServer(sub);
      this.subscription = null;
      console.debug('[Push] Unsubscribed successfully');
      return true;
    } catch (error) {
      console.error('[Push] Unsubscribe failed:', error);
      return false;
    }
  }

  // Send subscription to server
  async sendSubscriptionToServer(subscription) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    
    try {
      const response = await fetch('/api/v1/push/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token
        },
        body: JSON.stringify({
          subscription: subscription.toJSON()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save subscription');
      }

      console.debug('[Push] Subscription saved to server');
    } catch (error) {
      console.error('[Push] Failed to save subscription:', error);
    }
  }

  // Remove subscription from server
  async removeSubscriptionFromServer(subscription) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    const endpoint = subscription?.endpoint;
    
    try {
      const url = endpoint 
        ? `/api/v1/push/unsubscribe?endpoint=${encodeURIComponent(endpoint)}`
        : '/api/v1/push/unsubscribe';

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': token
        }
      });

      if (!response.ok) {
        throw new Error('Failed to remove subscription');
      }

      console.debug('[Push] Subscription removed from server');
    } catch (error) {
      console.error('[Push] Failed to remove subscription:', error);
    }
  }

  // Convert VAPID key from base64 to Uint8Array
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }

    return outputArray;
  }

  // Test notification
  async testNotification() {
    if (Notification.permission !== 'granted') {
      console.warn('[Push] Permission not granted');
      return;
    }

    const token = document.querySelector('meta[name="csrf-token"]')?.content;

    try {
      await fetch('/api/v1/push/test', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': token
        }
      });
      console.debug('[Push] Test notification sent');
    } catch (error) {
      console.error('[Push] Test notification failed:', error);
    }
  }
}

// Create global instance
window.pushManager = new PushNotificationManager();

// Auto-initialize on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.pushManager.init();
  });
} else {
  window.pushManager.init();
}

// Export for module usage
export default PushNotificationManager;
